import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, PieChart, Activity, Users, Database, Zap, Shield, ArrowRight, Play, Star } from 'lucide-react';
import Header from './header';
import Footer from './footer';
const DataAnalyticsPage = () => {
  const [animatedStats, setAnimatedStats] = useState({
    customers: 0,
    accuracy: 0,
    insights: 0,
    growth: 0
  });

  // Animate stats on component mount
  useEffect(() => {
    const targets = { customers: 2500, accuracy: 98, insights: 15000, growth: 45 };
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    const animate = (step) => {
      const progress = Math.min(step / steps, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedStats({
        customers: Math.floor(targets.customers * easeOut),
        accuracy: Math.floor(targets.accuracy * easeOut),
        insights: Math.floor(targets.insights * easeOut),
        growth: Math.floor(targets.growth * easeOut)
      });

      if (step < steps) {
        setTimeout(() => animate(step + 1), stepTime);
      }
    };

    animate(0);
  }, []);

  const chartData = [
    { icon: <BarChart3 className="w-12 h-12 text-blue-600" />, title: "Revenue Analytics", value: "+34%" },
    { icon: <TrendingUp className="w-12 h-12 text-green-600" />, title: "Growth Metrics", value: "+28%" },
    { icon: <PieChart className="w-12 h-12 text-purple-600" />, title: "Market Share", value: "67%" },
    { icon: <Activity className="w-12 h-12 text-orange-600" />, title: "Live Tracking", value: "Real-time" }
  ];

  const features = [
    {
      icon: <Database className="w-8 h-8 text-blue-400" />,
      title: "Advanced Data Processing",
      description: "Process millions of data points with lightning-fast algorithms and machine learning models that adapt to your business needs."
    },
    {
      icon: <Zap className="w-8 h-8 text-yellow-400" />,
      title: "Real-time Analytics",
      description: "Get instant insights with live data streaming and real-time dashboard updates that keep you ahead of the competition."
    },
    {
      icon: <Shield className="w-8 h-8 text-green-400" />,
      title: "Enterprise Security",
      description: "Bank-grade encryption and compliance with industry standards ensure your data remains protected at all times."
    },
    {
      icon: <Users className="w-8 h-8 text-purple-400" />,
      title: "Team Collaboration",
      description: "Share insights seamlessly across teams with collaborative workspaces, shared dashboards, and comprehensive reporting tools."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Data Science Director",
      company: "TechCorp",
      rating: 5,
      text: "This platform transformed how we handle data analytics. The insights are incredible and actionable."
    },
    {
      name: "Michael Rodriguez",
      role: "VP of Analytics",
      company: "InnovateLabs",
      rating: 5,
      text: "The real-time capabilities have given us a competitive edge. Our decision-making process is now data-driven."
    },
    {
      name: "Emily Johnson",
      role: "Business Intelligence Lead",
      company: "DataFlow Inc",
      rating: 5,
      text: "User-friendly interface with powerful analytics. Our entire team adopted it within days."
    }
  ];

  const capabilities = [
    "Providing an Experience-Centric approach to all touchpoints, architectures, stakeholders, and implementations",
    "Delivering predictive analytics through machine learning and AI-powered insights",
    "Creating end-to-end data pipelines with automated processing and validation",
    "Building comprehensive dashboards with interactive visualizations and real-time updates",
    "Enabling self-service analytics for business users with intuitive drag-and-drop interfaces"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Light */}
      <Header  />
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Data & <span className="text-blue-600">Analytics</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Unlock the power of your data with advanced analytics and machine learning. 
                Transform raw information into actionable insights that drive business growth 
                and competitive advantage through intelligent data processing.
              </p>
            </div>

            {/* Chart Widgets */}
            <div className="grid grid-cols-2 gap-6">
              {chartData.map((chart, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-100"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 p-3 bg-gray-50 rounded-full">
                      {chart.icon}
                    </div>
                    <h3 className="text-gray-900 font-semibold mb-2 text-sm">{chart.title}</h3>
                    <p className="text-2xl font-bold text-blue-600">{chart.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section - Dark */}
      <div className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-400 mb-2">{animatedStats.customers.toLocaleString()}+</div>
              <div className="text-gray-300 font-medium">Active Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-400 mb-2">{animatedStats.accuracy}%</div>
              <div className="text-gray-300 font-medium">Accuracy Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-400 mb-2">{animatedStats.insights.toLocaleString()}+</div>
              <div className="text-gray-300 font-medium">Insights Generated</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-400 mb-2">{animatedStats.growth}%</div>
              <div className="text-gray-300 font-medium">Revenue Growth</div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Statement - Light */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          {/* <h2 className="text-4xl font-bold text-gray-900 mb-8">The Challenge</h2>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            There are billions of terabytes of data generated on a daily basis and those enterprises who derive value by 
            discovering patterns from unstructured data and structured data sources are poised to gain a competitive advantage. 
            However, most organizations struggle with data silos, inconsistent analysis, and lack of real-time insights.
          </p> */}
          
          <div className="bg-blue-50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Solution</h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              We offer a comprehensive data analytics platform that unifies disparate data sources, applies advanced 
              machine learning algorithms, and delivers actionable insights through intuitive dashboards. Our platform 
              bridges the gap between raw data and strategic decision-making.
            </p>
          </div>
        </div>
      </div>

      {/* Features Section - Dark */}
      <div className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Data & Analytics Expertise</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Our advanced Data & Analytics practice offers expert solutions that unlock the full potential of your data. 
              We specialize in transforming complex datasets into strategic business intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group border border-gray-700 hover:border-gray-600"
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 p-3 bg-gray-700 rounded-lg group-hover:bg-gray-600 transition-colors">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Key Capabilities</h3>
            <div className="space-y-4">
              {capabilities.map((capability, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                  <p className="text-gray-300 leading-relaxed">{capability}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section - Light */}
      {/* <div className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how leading organizations are transforming their data strategies with our analytics platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                  <p className="text-sm text-blue-600 font-medium">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* CTA Section - Dark */}
      <div className="py-20 bg-blue-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Data Strategy?</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Join thousands of organizations already leveraging our platform to make data-driven decisions 
            that accelerate growth and innovation.
          </p>
           <a href="/Contact" className="">
          <button className="bg-white text-blue-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg">
            Get Started Today
            <ArrowRight className="w-5 h-5 ml-2 inline" />
          </button>
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DataAnalyticsPage;